import { Routes } from '@angular/router';
import { LoginGuard } from './_shared/guards/login.guard';

export const Routing: Routes = [
    { path: '', redirectTo: 'auth/login', pathMatch: 'full' },
    {
        path: 'auth',
        title: 'DoorSpot - Authentication',
        loadComponent: () =>
            import('./auth-layout/auth-layout.component').then(
                (m) => m.AuthLayoutComponent,
            ),
        loadChildren: () =>
            import('./auth-layout/auth-layout.routes').then(
                (m) => m.AUTH_LAYOUT_ROUTES,
            ),
    },
    {
        // RF-3066: the guard belongs HERE, on the parent that actually has
        // children. Every route inside these portals carried its own
        // `canActivateChild: [LoginGuard]`, but `canActivateChild` only governs a
        // route's CHILDREN and none of those leaves had any -- so 192 guard
        // declarations never ran once, and nothing checked for a token before a
        // signed-in page loaded. Declared on the lazy parent, it covers every
        // child, including the four client report routes that had been missed.
        canActivateChild: [LoginGuard],
        path: 'client',
        title: 'DoorSpot - Client Dashboard',
        loadComponent: () =>
            import('./client-layout/client-layout.component').then(
                (m) => m.ClientLayoutComponent,
            ),
        loadChildren: () =>
            import('./client-layout/client-layout.routes').then(
                (m) => m.CLIENT_LAYOUT_ROUTES,
            ),
    },
    {
        // RF-3066: the guard belongs HERE, on the parent that actually has
        // children. Every route inside these portals carried its own
        // `canActivateChild: [LoginGuard]`, but `canActivateChild` only governs a
        // route's CHILDREN and none of those leaves had any -- so 192 guard
        // declarations never ran once, and nothing checked for a token before a
        // signed-in page loaded. Declared on the lazy parent, it covers every
        // child, including the four client report routes that had been missed.
        canActivateChild: [LoginGuard],
        path: 'tenant',
        title: 'DoorSpot - Tenant Dashboard',
        loadComponent: () =>
            import('./tenant-layout/tenant-layout.component').then(
                (m) => m.TenantLayoutComponent,
            ),
        loadChildren: () =>
            import('./tenant-layout/tenant-layout.routes').then(
                (m) => m.TENANT_LAYOUT_ROUTES,
            ),
    },
    {
        // RF-3066: the guard belongs HERE, on the parent that actually has
        // children. Every route inside these portals carried its own
        // `canActivateChild: [LoginGuard]`, but `canActivateChild` only governs a
        // route's CHILDREN and none of those leaves had any -- so 192 guard
        // declarations never ran once, and nothing checked for a token before a
        // signed-in page loaded. Declared on the lazy parent, it covers every
        // child, including the four client report routes that had been missed.
        canActivateChild: [LoginGuard],
        path: 'owner',
        title: 'DoorSpot - Owner Dashboard',
        loadComponent: () =>
            import('./owner-layout/owner-layout.component').then(
                (m) => m.OwnerLayoutComponent,
            ),
        loadChildren: () =>
            import('./owner-layout/owner-layout.routes').then(
                (m) => m.OWNER_LAYOUT_ROUTES,
            ),
    },
    {
        // RF-3066: the guard belongs HERE, on the parent that actually has
        // children. Every route inside these portals carried its own
        // `canActivateChild: [LoginGuard]`, but `canActivateChild` only governs a
        // route's CHILDREN and none of those leaves had any -- so 192 guard
        // declarations never ran once, and nothing checked for a token before a
        // signed-in page loaded. Declared on the lazy parent, it covers every
        // child, including the four client report routes that had been missed.
        canActivateChild: [LoginGuard],
        path: 'service',
        title: 'DoorSpot - Service Dashboard',
        loadComponent: () =>
            import('./service-layout/service-layout.component').then(
                (m) => m.ServiceLayoutComponent,
            ),
        loadChildren: () =>
            import('./service-layout/service-layout.routes').then(
                (m) => m.SERVICE_LAYOUT_ROUTES,
            ),
    },
    /**
     * RF-3045 (T4.17): the account page, for a login with no role in any
     * company (`dashboard: 'account'`). Outside the portals on purpose: their
     * layouts bootstrap a company this login no longer has. Token only -- the
     * page reads nothing company-scoped.
     */
    {
        canActivateChild: [LoginGuard],
        path: 'account',
        title: 'DoorSpot - My Account',
        loadComponent: () =>
            import('./account-layout/account-layout.component').then(
                (m) => m.AccountLayoutComponent,
            ),
        loadChildren: () =>
            import('./account-layout/account-layout.routes').then(
                (m) => m.ACCOUNT_LAYOUT_ROUTES,
            ),
    },
    /** Streamlined tenant payment setup (logged-in tenant, no full account yet) */
    {
        path: 'onboarding',
        title: 'DoorSpot - Get Set Up',
        loadComponent: () =>
            import('./onboarding-layout/onboarding-layout.component').then(
                (m) => m.OnboardingLayoutComponent,
            ),
        loadChildren: () =>
            import('./onboarding-layout/onboarding-layout.routes').then(
                (m) => m.ONBOARDING_ROUTES,
            ),
    },
    /** Rental application; takes nonce from email click */
    {
        path: 'apply',
        title: 'DoorSpot - Apply',
        loadComponent: () =>
            import('./ad-hoc-layout/ad-hoc-layout.component').then(
                (m) => m.AdHocLayoutComponent,
            ),
        loadChildren: () =>
            import('./ad-hoc-layout/ad-hoc-layout.routes').then(
                (m) => m.APPLY_ROUTES,
            ),
    },
    /** Basic onboarding for owners to get signed up quickly */
    {
        path: 'get-started',
        title: 'DoorSpot - Onboarding',
        loadComponent: () =>
            import('./ad-hoc-layout/ad-hoc-layout.component').then(
                (m) => m.AdHocLayoutComponent,
            ),
        loadChildren: () =>
            import('./ad-hoc-layout/ad-hoc-layout.routes').then(
                (m) => m.GET_STARTED_ROUTES,
            ),
    },
    /** Retired public listings pages (RF-3022): every URL shows "no longer available". */
    {
        path: 'listings',
        title: 'DoorSpot - Listing unavailable',
        loadComponent: () =>
            import('./ad-hoc-layout/ad-hoc-layout.component').then(
                (m) => m.AdHocLayoutComponent,
            ),
        loadChildren: () =>
            import('./ad-hoc-layout/ad-hoc-layout.routes').then(
                (m) => m.LISTING_ROUTES,
            ),
    },
    {
        path: 'units',
        title: 'DoorSpot - Units',
        loadComponent: () =>
            import('./ad-hoc-layout/ad-hoc-layout.component').then(
                (m) => m.AdHocLayoutComponent,
            ),
        loadChildren: () =>
            import('./ad-hoc-layout/ad-hoc-layout.routes').then(
                (m) => m.RESERVATION_ROUTES,
            ),
    },
    /** Public forms pages */
    {
        path: 'forms',
        title: 'DoorSpot Form',
        loadComponent: () =>
            import('./ad-hoc-layout/ad-hoc-layout.component').then(
                (m) => m.AdHocLayoutComponent,
            ),
        loadChildren: () =>
            import('./ad-hoc-layout/ad-hoc-layout.routes').then(
                (m) => m.FORM_ROUTES,
            ),
    },
];
